import {
  IProgrammingGanttRow,
  IProgrammingGanttSchedule,
  ProgrammingGantt,
} from "@/components/programming-gantt/programming-gantt";
import { ProgrammingGanttRowSchedule } from "@/components/programming-gantt/programming-gantt-row-schedule";
import { $, createSVG } from "@/utils/selectors";

export class ProgrammingGanttRow<ROW extends IProgrammingGanttRow, SCHEDULE extends IProgrammingGanttSchedule> {
  private $group!: SVGElement | null;
  private $background!: SVGForeignObjectElement;

  private $schedules: Array<ProgrammingGanttRowSchedule<ROW, SCHEDULE>> = [];
  private translateX = 0;
  private translateY = 0;
  private isVisible: boolean = true;

  constructor(
    private readonly gantt: ProgrammingGantt<ROW, SCHEDULE>,
    private readonly row: ROW,
    private readonly schedules: Array<SCHEDULE>,
    private readonly $container: SVGElement,
  ) {
    this.handleVisibility();
    this.updateRender();
  }

  private mount() {
    if (this.$group) {
      return;
    }

    this.$group = createSVG("g", {
      append_to: this.$container,
      y: (this.row.level ?? 0) * this.gantt.getOptions().rowHeight,
      x: 0,
      width: "100%",
      height: `${this.gantt.getOptions().rowHeight}px`,
      level: this.row.level ?? 0,
      "row-id": this.row.id,
      class: "gantt-row",
      ["data-test-id"]: "gantt-row",
    });
    $.attr(this.$group, "transform", `translate(${this.translateX}, 0)`);

    this.$background = createSVG("foreignObject", {
      append_to: this.$group,
      x: 0,
      y: (this.row.level ?? 0) * this.gantt.getOptions().rowHeight,
      width: "100%",
      height: `${this.gantt.getOptions().rowHeight}px`,
      class: "gantt-row__background",
    });
    $.attr(this.$background, "transform", `translate(${-this.translateX}, 0)`);

    this.$schedules = [];
    for (const schedule of this.schedules) {
      this.$schedules.push(new ProgrammingGanttRowSchedule(this.gantt, this, schedule));
    }

    this.isVisible = this.gantt.isRowVisible(this.row);

    this.bindEventListeners();
    this.updateRender();
  }

  private bindEventListeners() {
    this.$group?.addEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.$group?.addEventListener("mouseleave", this.onMouseLeave.bind(this));
    this.$group?.addEventListener("click", this.onMouseClick.bind(this));
  }

  private unbindEventListeners() {
    this.$group?.removeEventListener("mouseenter", this.onMouseEnter.bind(this));
    this.$group?.removeEventListener("mouseleave", this.onMouseLeave.bind(this));
    this.$group?.removeEventListener("click", this.onMouseClick.bind(this));
  }

  private onMouseEnter() {
    this.$group!.classList.add("highlight");
    document.querySelectorAll(`.gantt-left-panel__row[row-id="${this.row.id}"]`).forEach((ganttRow) => {
      ganttRow.classList.add("highlight");
    });
  }

  private onMouseLeave() {
    this.$group!.classList.remove("highlight");
    document.querySelectorAll(`.gantt-left-panel__row[row-id="${this.row.id}"]`).forEach((ganttRow) => {
      ganttRow.classList.remove("highlight");
    });
  }

  private onMouseClick() {
    if (this.gantt.isDragging()) {
      return;
    }

    this.gantt.toggleRowSelection(this.row.id);
  }

  private unmount() {
    if (!this.$group) {
      return;
    }

    for (const schedule of this.$schedules) {
      schedule.remove();
    }

    this.unbindEventListeners();

    this.$group.remove();
    this.$group = null;
  }

  public group() {
    return this.$group;
  }

  public getRow() {
    return { ...this.row };
  }

  public resetPosition(x: number = 0, y: number = 0) {
    this.translateX = x;
    this.translateY = y;

    if (!this.$group) {
      return;
    }

    $.attr(this.$group, "transform", `translate(${this.translateX}, ${this.translateY})`);
  }

  public moveXBy(number: number) {
    this.translateX = number;
    this.handleVisibility();

    if (!this.$group) {
      return;
    }

    $.attr(this.$group, "transform", `translate(${this.translateX}, ${this.translateY})`);
    $.attr(this.$background, "transform", `translate(${-this.translateX}, 0)`);
  }

  public moveYBy(number: number) {
    this.translateY = -number;
    this.handleVisibility();

    if (!this.$group) {
      return;
    }

    $.attr(this.$group, "transform", `translate(${this.translateX}, ${this.translateY})`);
  }

  private handleVisibility() {
    if (this.isInViewport()) {
      this.mount();

      for (const $schedule of this.$schedules) {
        $schedule.handleVisibility();
      }
    } else {
      this.unmount();
    }
  }

  private isInViewport() {
    const viewport = this.gantt.getVisibleViewport();
    const lineTop = this.y();
    const lineBottom = this.bottom();

    return (
      (lineTop >= viewport.y && lineTop <= viewport.bottom) || // Top rect is visible
      (lineBottom >= viewport.y && lineBottom <= viewport.bottom) || // Bottom rect is visible
      (lineTop <= viewport.y && lineBottom >= viewport.bottom) // Line covers the viewport
    );
  }

  public y() {
    return (this.row.level ?? 0) * this.gantt.getRowHeight();
  }

  public bottom() {
    return this.y() + this.gantt.getRowHeight();
  }

  public destroy() {
    this.unbindEventListeners();
    this.unmount();
    this.$group?.remove();
  }

  public hide() {
    this.isVisible = false;
    this.updateRender();
  }

  public show() {
    this.isVisible = true;
    this.updateRender();
  }

  public updateRender() {
    this.$schedules.forEach((schedule) => {
      if (this.isVisible) {
        schedule.show();
      } else {
        schedule.hide();
      }
    });
    this.$background?.setAttribute("visible", `${this.isVisible}`);
  }
}
